import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const formatDateTime = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "\u2014";

export default function Alerts() {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("unresolved");

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      params.unresolved_only = tab === "unresolved";
      const res = await axios.get(`${API}/alerts`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(res.data.alerts || []);
    } catch (err) {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, [token, tab]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleResolve = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Alert resolved");
      fetchAlerts();
    } catch (err) {
      toast.error("Failed to resolve alert");
    }
  };

  const severityCounts = {
    all: alerts.length,
    critical: alerts.filter((a) => a.severity === "critical").length,
    warning: alerts.filter((a) => a.severity === "warning").length,
    info: alerts.filter((a) => a.severity === "info").length,
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="alerts-title">Alerts</h1>
        <p className="text-sm text-muted-foreground mt-1">Operational exceptions and issues</p>
      </div>

      <div className="flex gap-2">
        <Button variant={tab === "unresolved" ? "default" : "outline"} size="sm" onClick={() => setTab("unresolved")} data-testid="alerts-tab-unresolved">Unresolved</Button>
        <Button variant={tab === "all" ? "default" : "outline"} size="sm" onClick={() => setTab("all")} data-testid="alerts-tab-all">All</Button>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {Object.entries(severityCounts).map(([sev, count]) => (
          <Card key={sev} className="kpi-card">
            <CardContent className="pt-0 pb-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{sev}</p>
              <p className="font-display text-xl font-semibold mt-1 tabular-nums">{count}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map((key) => (
                <div key={`skeleton-${key}`} className="h-20 bg-muted rounded animate-pulse" />
              ))}
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-muted-foreground">No alerts to show</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`flex items-start gap-4 p-4 rounded-lg border ${
                    alert.is_resolved ? 'bg-muted/20 opacity-60' : 'bg-card'
                  }`}
                  data-testid={`alert-${alert.id}`}
                >
                  <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${
                    alert.severity === "critical" ? "text-rose-500" :
                    alert.severity === "warning" ? "text-amber-500" : "text-sky-500"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <StatusBadge status={alert.alert_type} />
                      <StatusBadge status={alert.severity} />
                      {alert.is_resolved && <StatusBadge status="synced" />}
                    </div>
                    <p className="text-sm font-medium">{alert.message}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span>{formatDateTime(alert.created_at)}</span>
                      {alert.dealer_name && <span>Dealer: {alert.dealer_name}</span>}
                    </div>
                  </div>
                  {!alert.is_resolved && (
                    <Button variant="outline" size="sm" onClick={() => handleResolve(alert.id)} data-testid={`alerts-resolve-${alert.id}`}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Resolve
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
